import { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

// Contexto de autenticación
interface AuthContextType {
    user: User | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<{ error: any }>;
    signOut: () => Promise<{ error: any }>;
    resetPassword: (email: string) => Promise<{ error: any }>;
    updatePassword: (newPassword: string) => Promise<{ error: any }>;
    impersonate: (userId: string) => Promise<void>;
    stopImpersonation: () => void;
    isImpersonating: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider de autenticación
export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
    const [originalAdminUser, setOriginalAdminUser] = useState<User | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Obtener sesión inicial
        const initAuth = async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.user) {
                    localStorage.setItem('supabase.auth.token', session.access_token);

                    // Check for active impersonation
                    const storedImpersonatedId = localStorage.getItem('impersonated_user_id');
                    const storedOriginalAdmin = localStorage.getItem('original_admin_json');

                    if (storedImpersonatedId && storedOriginalAdmin) {
                        try {
                            const originalUser = JSON.parse(storedOriginalAdmin);
                            setOriginalAdminUser(originalUser);
                            setImpersonatedUserId(storedImpersonatedId);

                            const impersonatedUser = {
                                ...session.user,
                                id: storedImpersonatedId,
                                email: 'impersonated@user.com',
                                user_metadata: { ...session.user.user_metadata, is_impersonated: true }
                            };
                            setUser(impersonatedUser as User);
                        } catch (e) {
                            console.error('Error restoring impersonation', e);
                            localStorage.removeItem('impersonated_user_id');
                            localStorage.removeItem('original_admin_json');
                            setUser(session.user);
                        }
                    } else {
                        // Setear usuario inmediatamente (sin bloquear)
                        setUser(session.user);
                    }

                    // Aceptar invitaciones pendientes en BACKGROUND (fire-and-forget)
                    Promise.resolve(supabase.rpc('accept_team_invitation')).then(({ data: inviteResult }) => {
                        if (inviteResult?.success) {
                            console.log('✅ Invitación aceptada:', inviteResult);
                            toast.success(`¡Te has unido al equipo! Rol: ${inviteResult.role}`, { icon: '👏', duration: 4000 });
                        }
                    }).catch(() => {
                        // Silencioso
                    });
                }
            } catch (error) {
                console.error('Error al verificar sesión:', error);
            } finally {
                setLoading(false);
            }
        };

        initAuth();

        // Escuchar cambios de autenticación
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                console.log('Auth event:', event, session?.user?.email);

                // ⚠️ IMPORTANTE: Ignorar eventos que no son de sesión
                if (event === 'PASSWORD_RECOVERY' || event === 'USER_UPDATED') {
                    console.log('⚠️ Ignorando evento:', event);
                    return;
                }

                // INITIAL_SESSION: Se dispara al cargar la página. 
                // initAuth() ya maneja esto, solo actualizar token si es necesario.
                if (event === 'INITIAL_SESSION') {
                    if (session?.access_token) {
                        localStorage.setItem('supabase.auth.token', session.access_token);
                    }
                    return;
                }

                // TOKEN_REFRESHED: Solo actualizar el token, NO re-setear el usuario
                // Re-setear user causa re-renders innecesarios en toda la app
                if (event === 'TOKEN_REFRESHED') {
                    if (session?.access_token) {
                        localStorage.setItem('supabase.auth.token', session.access_token);
                    }
                    return;
                }

                // SIGNED_IN: Usuario acaba de iniciar sesión
                if (event === 'SIGNED_IN') {
                    if (session?.user) {
                        setUser(session.user);

                        if (session.access_token) {
                            localStorage.setItem('supabase.auth.token', session.access_token);
                        }

                        // Navegar a dashboard
                        if (window.location.pathname === '/login') {
                            navigate('/');
                        }

                        // Aceptar invitación en background (fire-and-forget)
                        Promise.resolve(supabase.rpc('accept_team_invitation')).then(({ data: inviteResult }) => {
                            if (inviteResult?.success) {
                                console.log('✅ Invitación aceptada:', inviteResult);
                                toast.success(`¡Bienvenido al equipo! Rol: ${inviteResult.role}`, { icon: '👏', duration: 4000 });
                            }
                        }).catch(() => { });
                    }
                    return;
                }

                // SIGNED_OUT / USER_DELETED
                if (event === 'SIGNED_OUT') {
                    setUser(null);
                    localStorage.removeItem('supabase.auth.token');

                    const publicPaths = ['/login', '/pricing', '/store/', '/tienda/', '/menu/', '/order-status/'];
                    const currentPath = window.location.pathname;
                    const isPublicPath = publicPaths.some(path => currentPath.includes(path));

                    if (!isPublicPath) {
                        navigate('/login');
                    }
                }
            }
        );

        // Cleanup
        return () => {
            subscription.unsubscribe();
        };
    }, [navigate]);

    const signIn = async (email: string, password: string) => {
        try {
            if (!email.trim() || !password.trim()) {
                throw new Error('Por favor, ingrese su correo y contraseña');
            }

            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) {
                if (error.message === 'Invalid login credentials') {
                    throw new Error('Correo o contraseña incorrectos');
                }
                if (error.message.includes('Email not confirmed')) {
                    throw new Error('Por favor, confirme su correo electrónico');
                }
                throw error;
            }

            if (data?.user) {
                // El listener de onAuthStateChange se encargará de actualizar el estado y navegar
                toast.success('Sesión iniciada correctamente');
                return { error: null };
            }

            throw new Error('Error al iniciar sesión');
        } catch (error: any) {
            console.error('Error de autenticación:', error);
            toast.error(error.message || 'Error al iniciar sesión');
            return { error };
        }
    };

    const signOut = async () => {
        try {
            // Guardar el email recordado antes de limpiar
            const rememberedEmail = localStorage.getItem('rememberedEmail');

            await supabase.auth.signOut({ scope: 'global' });
            localStorage.clear();

            // Restaurar el email guardado si existía
            if (rememberedEmail) {
                localStorage.setItem('rememberedEmail', rememberedEmail);
            }

            document.cookie.split(";").forEach(function (c) {
                document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
            });
            toast.success('Sesión cerrada correctamente');
            return { error: null };
        } catch (error: any) {
            console.error('Error al cerrar sesión:', error);

            // Guardar el email recordado antes de limpiar (también en caso de error)
            const rememberedEmail = localStorage.getItem('rememberedEmail');

            setUser(null);
            localStorage.clear();

            // Restaurar el email guardado
            if (rememberedEmail) {
                localStorage.setItem('rememberedEmail', rememberedEmail);
            }

            navigate('/login');
            return { error };
        }
    };

    const resetPassword = async (email: string) => {
        try {
            if (!email.trim()) {
                throw new Error('Por favor, ingrese su correo electrónico');
            }

            // ⚠️ DESACTIVADO: Sistema de email de Supabase
            // Ahora usamos SOLO el sistema personalizado de WhatsApp
            // que se maneja directamente en Login.tsx

            /* SISTEMA ANTIGUO (DESACTIVADO):
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/login?mode=reset`
            });

            if (error) {
                if (error.message.includes('Email rate limit exceeded')) {
                    throw new Error('Has enviado demasiadas solicitudes. Por favor, espera unos minutos.');
                }
                throw error;
            }

            toast.success('Se ha enviado un enlace de recuperación a tu correo');
            */

            // El sistema de WhatsApp se maneja en el modal de Login.tsx
            // Esta función ya no envía emails, solo valida el email
            // El modal se encarga de buscar el usuario y enviar por WhatsApp

            return { error: null };
        } catch (error: any) {
            console.error('Error en resetPassword:', error);
            toast.error(error.message || 'Error al procesar la solicitud');
            return { error };
        }
    };

    const updatePassword = async (newPassword: string) => {
        try {
            if (!newPassword || newPassword.length < 6) {
                throw new Error('La contraseña debe tener al menos 6 caracteres');
            }

            const { error } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (error) {
                if (error.message.includes('Auth session missing')) {
                    throw new Error('Sesión expirada. Por favor, solicita un nuevo enlace de recuperación.');
                }
                throw error;
            }

            toast.success('Contraseña actualizada correctamente');
            navigate('/login');
            return { error: null };
        } catch (error: any) {
            console.error('Error al actualizar contraseña:', error);
            toast.error(error.message || 'Error al actualizar contraseña');
            return { error };
        }
    };

    const impersonate = async (userId: string) => {
        if (!user) return;

        // Save original admin if not already saved (in case of nested calls, though UI should prevent)
        if (!originalAdminUser) {
            setOriginalAdminUser(user);
            localStorage.setItem('original_admin_json', JSON.stringify(user));
        }

        // Set impersonation state
        setImpersonatedUserId(userId);
        localStorage.setItem('impersonated_user_id', userId);

        // Create fake user object
        // We carefully preserve the JWT token capabilities (implicitly via the client) 
        // but change the ID so frontend queries fetch the target's data.
        // NOTE: This relies on RLS policies allowing the Admin (auth.uid()) to access data of 'userId'.
        const impersonatedUser = {
            ...user,
            id: userId,
            email: 'impersonated@user.com',
            user_metadata: { ...user.user_metadata, is_impersonated: true }
        };

        setUser(impersonatedUser as User);
        toast.success('Modo de suplantación activado');
        navigate('/dashboard');
    };

    const stopImpersonation = () => {
        const storedOriginal = localStorage.getItem('original_admin_json');

        if (storedOriginal) {
            try {
                const originalUser = JSON.parse(storedOriginal);
                setUser(originalUser);
            } catch (e) {
                // Determine recovery via reload
                window.location.reload();
                return;
            }
        } else {
            // Fallback
            window.location.reload();
            return;
        }

        setImpersonatedUserId(null);
        setOriginalAdminUser(null);
        localStorage.removeItem('impersonated_user_id');
        localStorage.removeItem('original_admin_json');

        toast.success('Sesión de administrador restaurada');
        navigate('/sa-control-panel'); // Return to admin panel
    };

    const value = {
        user,
        loading,
        signIn,
        signOut,
        resetPassword,
        updatePassword,
        impersonate,
        stopImpersonation,
        isImpersonating: !!impersonatedUserId
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// Hook para usar el contexto
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
